import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import apiConfig from "../../../apiConfig";

function CodOrders() {
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(50);
    const [expandedTitle, setExpandedTitle] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [searchOrderId, setSearchOrderId] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    const fetchOrders = async (page = 1) => {
        try {
            const res = await fetch(
                `${apiConfig.baseUrl}${apiConfig.GetAllCodOrders}?page=${page}`
            );
            const data = await res.json();
            if (data.orders) {
                setOrders(data.orders);
                setTotalPages(data.totalPages);
                setCurrentPage(page); // reset to requested page
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
        }
    };

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage]);

    const downloadExcel = () => {
        const formattedOrders = orders.map((order) => ({
            "Order ID": order.orderId,
            "Product Titles": order.items.map((i) => i.title).join(", "),
            Quantity: order.items.reduce((sum, i) => sum + i.quantity, 0),

            // add price per product (comma separated if multiple items)
            " Prices": order.items.map((i) => i.price).join(", "),
            // Optional: sum of item prices (just product price x qty, before COD & shipping)
            " Total": order.items.reduce((sum, i) => sum + i.price * i.quantity, 0),

            "COD Charge": order.codCharge || 100,
            "Shipping Cost": order.shippingCost,

            // Order level total price
            "Total Price": order.totalPrice,

            "Payment Method": order.paymentMethod,
            Status: order.status,
            "Order Date": new Date(order.orderDate).toLocaleString("en-IN"),
            "Customer Name": `${order.billingAddress?.firstName || ""} ${order.billingAddress?.lastName || ""
                }`,
            Email: order.billingAddress?.email || "",
            Phone: order.billingAddress?.phone || "",
            Address: `${order.billingAddress?.streetAddress || ""}, ${order.billingAddress?.city || ""
                }, ${order.billingAddress?.state || ""} - ${order.billingAddress?.pinCode || ""
                }`,
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedOrders);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "COD Orders");

        XLSX.writeFile(workbook, "COD_Orders.xlsx");
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            setUpdatingStatusId(orderId);
            const res = await fetch(
                `${apiConfig.baseUrl}${apiConfig.UpdateCODStatus}/${orderId}`,
                {
                    method: "put",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ status: newStatus }),
                }
            );

            if (!res.ok) throw new Error("Failed to update status");

            setOrders((prev) =>
                prev.map((order) =>
                    order.orderId === orderId ? { ...order, status: newStatus } : order
                )
            );
            alert("Status updated successfully!");
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Failed to update status");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const searchOrder = async () => {
        if (!searchOrderId) {
            alert("Please enter an Order ID");
            return;
        }
        try {
            setIsSearching(true);
            const res = await fetch(
                `${apiConfig.baseUrl}/superAdmin/SearchOrder/${searchOrderId}`
            );
            if (!res.ok) throw new Error("Order not found");
            const data = await res.json();
            if (data.order) {
                setOrders([data.order]); // Replace the list with the searched order
            } else {
                setOrders([]);
                alert("Order not found");
            }
        } catch (err) {
            console.error("Error searching order:", err);
            alert("Error searching order");
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div style={{ padding: "20px", fontFamily: "Arial" }}>
            <h2 style={{ textAlign: "center" }}>COD Orders</h2>

            <div style={{ textAlign: "left", marginBottom: "10px" }}>
                <button
                    onClick={downloadExcel}
                    style={{
                        padding: "8px 16px",
                        background: "#4CAF50",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    Download Cod Order
                </button>
            </div>

            <div
                style={{
                    marginBottom: "20px",
                    display: "flex",
                    gap: "8px",
                    width: "500px",
                }}
            >
                <input
                    type="text"
                    placeholder="Enter Order ID"
                    value={searchOrderId}
                    onChange={(e) => setSearchOrderId(e.target.value)}
                    style={{
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        flex: "1",
                    }}
                />
                <button
                    onClick={searchOrder}
                    disabled={isSearching}
                    style={{
                        padding: "8px 16px",
                        background: "#2196F3",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    {isSearching ? "Searching..." : "Search"}
                </button>
                <button
                    onClick={() => {
                        setSearchOrderId(""); // clear input
                        fetchOrders(1); // fetch first page of all orders
                    }}
                    style={{
                        padding: "8px 16px",
                        background: "#f44336",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    Clear
                </button>
            </div>

            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "20px",
                    background: "#fff",
                }}
            >
                <thead>
                    <tr style={{ backgroundColor: "#f2f2f2" }}>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Order ID
                        </th>
                        <th
                            style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                width: "700px",
                            }}
                        >
                            Products
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Cod Price
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Shiiping Cost
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Total Price
                        </th>

                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Order Date
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Customer
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Email</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Phone</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Address
                        </th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Payment
                        </th>

                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>Status</th>
                        <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                            Update Status
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((order, idx) => (
                        <tr key={order.orderId + idx}>
                            {/* Order ID */}
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.orderId}
                            </td>
                            {/* Products Nested Table */}
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        background: "#fafafa",
                                    }}
                                >
                                    <thead>
                                        <tr style={{ background: "#eee" }}>
                                            <th style={{ border: "1px solid #ccc", padding: "5px" }}>
                                                Image
                                            </th>
                                            <th
                                                style={{
                                                    border: "1px solid #ccc",
                                                    padding: "5px",
                                                    width: "80%",
                                                }}
                                            >
                                                Title
                                            </th>
                                            <th style={{ border: "1px solid #ccc", padding: "5px" }}>
                                                Qty
                                            </th>
                                            <th style={{ border: "1px solid #ccc", padding: "5px" }}>
                                                Price
                                            </th>
                                            <th style={{ border: "1px solid #ccc", padding: "5px" }}>
                                                Total
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items.map((item, i) => (
                                            <tr key={i}>
                                                <td
                                                    style={{
                                                        border: "1px solid #ccc",
                                                        padding: "5px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <img
                                                        src={item.image}
                                                        alt={item.title}
                                                        style={{
                                                            width: "50px",
                                                            height: "50px",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                </td>
                                                <td
                                                    style={{ border: "1px solid #ccc", padding: "5px" }}
                                                >
                                                    {/* ✅ Title with 4 line clamp + View More */}
                                                    <div
                                                        style={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 4,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            maxWidth: "500px",
                                                        }}
                                                    >
                                                        {item.title}
                                                    </div>
                                                    {item.title.length > 30 && (
                                                        <button
                                                            style={{
                                                                color: "#5DADE2", // halka blue
                                                                cursor: "pointer",
                                                                border: "none",
                                                                background: "transparent",
                                                                fontSize: "12px", // chhoti size
                                                                textAlign: "right", // right align
                                                                display: "block", // puri width le
                                                                padding: "0",
                                                                marginTop: "4px",
                                                            }}
                                                            onClick={() => setExpandedTitle(item.title)}
                                                        >
                                                            View More
                                                        </button>
                                                    )}
                                                </td>
                                                <td
                                                    style={{
                                                        border: "1px solid #ccc",
                                                        padding: "5px",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {item.quantity}
                                                </td>
                                                <td
                                                    style={{ border: "1px solid #ccc", padding: "5px" }}
                                                >
                                                    ₹{item.price}
                                                </td>
                                                <td
                                                    style={{ border: "1px solid #ccc", padding: "5px" }}
                                                >
                                                    ₹{item.total}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </td>
                            {/* Rest of the Order Details */}
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order?.codCharge || 100}
                            </td>

                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order?.shippingCost}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                ₹{order.totalPrice}
                            </td>

                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {new Date(order.orderDate).toLocaleString("en-IN")}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.billingAddress?.firstName}{" "}
                                {order.billingAddress?.lastName}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.billingAddress?.email}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.billingAddress?.phone}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.billingAddress?.streetAddress},{" "}
                                {order.billingAddress?.city}, {order.billingAddress?.state} -{" "}
                                {order.billingAddress?.pinCode}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.paymentMethod}
                            </td>

                            <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                {order.status}
                            </td>
                            <td
                                style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    textAlign: "center",
                                }}
                            >
                                <select
                                    value={
                                        updatingStatusId === order.orderId
                                            ? "changing"
                                            : order.status || ""
                                    }
                                    onChange={(e) => {
                                        const newStatus = e.target.value;
                                        updateOrderStatus(order.orderId, newStatus);
                                    }}
                                    disabled={updatingStatusId === order.orderId}
                                    style={{
                                        padding: "10px 14px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        backgroundColor: "#ffffff",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        color: "#374151",
                                        cursor:
                                            updatingStatusId === order.orderId ? "wait" : "pointer",
                                        outline: "none",
                                        minWidth: "120px",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#4CAF50";
                                        e.target.style.boxShadow =
                                            "0 0 0 3px rgba(76, 175, 80, 0.3)";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#d1d5db";
                                        e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                    }}
                                >
                                    <option value="">Select Status</option>
                                    <option value="Dispatched">Dispatched</option>
                                    <option value="in-transit">In-Transit</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="canceled">CanceledI</option>
                                    <option value="RTO In-Transit">RTO In-Transit</option>
                                    <option value="Returned">Returned</option>
                                    <option value="Lost">Lost</option>

                                    {updatingStatusId === order.orderId && (
                                        <option value="changing">Changing...</option>
                                    )}
                                </select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ✅ Modal */}
            {expandedTitle && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: "20px",
                            borderRadius: "8px",
                            maxWidth: "600px",
                            textAlign: "center",
                        }}
                    >
                        <h3>Full Title</h3>
                        <p>{expandedTitle}</p>
                        <button
                            onClick={() => setExpandedTitle(null)}
                            style={{
                                marginTop: "10px",
                                padding: "6px 12px",
                                cursor: "pointer",
                            }}
                        >
                            View Less
                        </button>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {/* <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => setCurrentPage(i + 1)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              backgroundColor: currentPage === i + 1 ? "#4CAF50" : "#fff",
              color: currentPage === i + 1 ? "#fff" : "#000",
              cursor: "pointer",
              borderRadius: "4px",
            }}
          >
            {i + 1}
          </button>
        ))}
      </div> */}
            <div
                style={{
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                }}
            >
                {/* Previous Button */}
                <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        backgroundColor: "#fff",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        borderRadius: "4px",
                        color: "black",
                    }}
                >
                    Prev
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => {
                    // Show only 5 pages at a time
                    if (i + 1 >= currentPage - 2 && i + 1 <= currentPage + 2) {
                        return (
                            <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                style={{
                                    padding: "8px 12px",
                                    border: "1px solid #ddd",
                                    backgroundColor: currentPage === i + 1 ? "#4CAF50" : "#fff",
                                    color: currentPage === i + 1 ? "#fff" : "#000",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                }}
                            >
                                {i + 1}
                            </button>
                        );
                    }
                    return null;
                })}

                {/* Next Button */}
                <button
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        backgroundColor: "#fff",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        borderRadius: "4px",
                        color: "black",
                    }}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default CodOrders;
